/**
 * OS05 — normalização e validação de chave Pix (DICT).
 * cpf/cnpj → dígitos | phone → E.164 | email → lower trim | random → UUID v4 lower
 */
import type { PixKeyType } from '../types';

export function onlyDigits(v: string): string {
  return String(v || '').replace(/\D/g, '');
}

function cpfCheckDigits(digits: string): boolean {
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
  let d1 = (sum * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== Number(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(digits[i]) * (11 - i);
  let d2 = (sum * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === Number(digits[10]);
}

function cnpjCheckDigits(digits: string): boolean {
  if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return false;
  const calc = (base: string, weights: number[]) => {
    const sum = base.split('').reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = calc(digits.slice(0, 12), w1);
  const d2 = calc(digits.slice(0, 12) + String(d1), w2);
  return d1 === Number(digits[12]) && d2 === Number(digits[13]);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** Normaliza telefone BR para E.164 (+55…). */
export function normalizePhoneE164(raw: string): string | null {
  let d = onlyDigits(raw);
  if (!d) return null;
  if (d.startsWith('55') && d.length >= 12) {
    // already with country
  } else if (d.length === 10 || d.length === 11) {
    d = `55${d}`;
  } else {
    return null;
  }
  if (d.length < 12 || d.length > 13) return null;
  return `+${d}`;
}

export function normalizePixKey(
  keyType: PixKeyType,
  raw: string
): { ok: true; key: string; keyRaw: string } | { ok: false; error: string } {
  const keyRaw = String(raw ?? '');
  const trimmed = keyRaw.trim();
  if (!trimmed) return { ok: false, error: 'Informe a chave Pix.' };

  switch (keyType) {
    case 'cpf': {
      const d = onlyDigits(trimmed);
      if (d.length !== 11 || !cpfCheckDigits(d)) {
        return { ok: false, error: 'CPF inválido. Confira os dígitos.' };
      }
      return { ok: true, key: d, keyRaw };
    }
    case 'cnpj': {
      const d = onlyDigits(trimmed);
      if (d.length !== 14 || !cnpjCheckDigits(d)) {
        return { ok: false, error: 'CNPJ inválido. Confira os dígitos.' };
      }
      return { ok: true, key: d, keyRaw };
    }
    case 'phone': {
      const e164 = normalizePhoneE164(trimmed);
      if (!e164) {
        return { ok: false, error: 'Celular inválido. Use DDD + número (ex.: 85999990000).' };
      }
      return { ok: true, key: e164, keyRaw };
    }
    case 'email': {
      const key = trimmed.toLowerCase();
      if (!EMAIL_RE.test(key)) {
        return { ok: false, error: 'E-mail inválido.' };
      }
      return { ok: true, key, keyRaw };
    }
    case 'random': {
      const key = trimmed.toLowerCase();
      if (!UUID_V4_RE.test(key)) {
        return { ok: false, error: 'Chave aleatória inválida. Use o UUID v4 do banco.' };
      }
      return { ok: true, key, keyRaw };
    }
    default:
      return { ok: false, error: 'Tipo de chave inválido.' };
  }
}

/** Compara chave celular com telefone de contato (anti-WhatsApp). */
export function isPixPhoneSameAsContact(pixKeyE164: string, contactPhone?: string | null): boolean {
  if (!contactPhone) return false;
  const a = onlyDigits(pixKeyE164);
  const b = onlyDigits(contactPhone);
  if (!a || !b) return false;
  const core = (d: string) => {
    let x = d;
    if (x.startsWith('55') && x.length >= 12) x = x.slice(2);
    return x;
  };
  const ca = core(a);
  const cb = core(b);
  return ca === cb || ca.endsWith(cb) || cb.endsWith(ca);
}

export function maskPixInput(keyType: PixKeyType, raw: string): string {
  if (keyType === 'cpf') {
    const d = onlyDigits(raw).slice(0, 11);
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  if (keyType === 'cnpj') {
    const d = onlyDigits(raw).slice(0, 14);
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  if (keyType === 'phone') {
    const d = onlyDigits(raw).slice(0, 13);
    if (d.length <= 11) {
      return d
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }
    return d;
  }
  return raw;
}

/** Chave no formato oficial do Pix (DICT) + titular, para montar o Pix copia e cola. */
export function getPixPayloadKey(
  payment?: { pix?: { status?: string; key?: string; holderName?: string } } | null
): { key: string; holderName?: string } | undefined {
  const pix = payment?.pix;
  if (!pix || pix.status !== 'confirmed') return undefined;
  const key = String(pix.key || '').trim();
  if (!key) return undefined;
  return { key, holderName: pix.holderName || undefined };
}

export function getConfirmedPixDisplayKey(payment?: { pix?: { status?: string; key?: string; keyRaw?: string } } | null): string | undefined {
  const pix = payment?.pix;
  if (!pix || pix.status === 'revoked') return undefined;
  const key = String(pix.keyRaw || pix.key || '').trim();
  return key || undefined;
}
